/**
 * Flow-rate l/s <-> m³/h (factor 3.6). Matches DB seeds: l_s_per_second, m3_h_per_hour, etc.
 */

const LABEL_M3H = "m³/h";
const LABEL_LS = "l/s";

function normalizePermalink(permalink) {
  if (!permalink) return "";
  return String(permalink)
    .toLowerCase()
    .replace(/\s+/g, "")
    .replace(/-/g, "_");
}

function normalizeTitleKey(text) {
  if (!text) return "";
  return String(text)
    .toLowerCase()
    .replace(/\s+/g, "")
    .replace(/³/g, "3")
    .replace(/²/g, "2");
}

const LS_PERMALINKS = new Set([
  "ls",
  "l/s",
  "l_s",
  "l_s_per_second",
  "liters_per_second",
]);

const M3H_PERMALINKS = new Set([
  "m3h",
  "m3/h",
  "m3_h",
  "m3_h_per_hour",
  "m3hperhour",
  "m3perhour",
]);

/**
 * @param {object|null|undefined} unit — { permalink?, title?, short_title? }
 * @returns {'ls' | 'm3h' | null}
 */
export function getFlowRateUnitKind(unit) {
  if (!unit) return null;

  let p = normalizePermalink(unit.permalink);
  p = p.replace(/³/g, "3");
  if (LS_PERMALINKS.has(p)) return "ls";
  if (M3H_PERMALINKS.has(p)) return "m3h";

  for (const raw of [unit.title, unit.short_title]) {
    const t = normalizeTitleKey(raw);
    if (!t) continue;
    if (t === "ls" || t === "l/s" || t.includes("l/s")) return "ls";
    if (t.includes("m3/h") || t === "m3h") return "m3h";
  }

  return null;
}

function formatPrimaryNb(n) {
  const x = Number(n);
  if (Number.isNaN(x)) return "";
  return new Intl.NumberFormat("nb-NO", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(x);
}

function formatConvertedNb(n) {
  const x = Number(n);
  if (Number.isNaN(x)) return "";
  const rounded = Math.round(x * 100) / 100;
  if (Math.abs(rounded - Math.round(rounded)) < 1e-9) {
    return new Intl.NumberFormat("nb-NO", {
      maximumFractionDigits: 0,
    }).format(Math.round(rounded));
  }
  return new Intl.NumberFormat("nb-NO", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(rounded);
}

/**
 * @param {object|null} unit
 * @param {string|number|null} value
 */
export function formatUnitDisplay(unit, value = null) {
  if (!unit) return "-";
  const title = unit.title || "";
  const kind = getFlowRateUnitKind(unit);

  if (value !== null && value !== undefined && value !== "") {
    const numValue = parseFloat(value);
    if (!Number.isNaN(numValue)) {
      const primary = formatPrimaryNb(numValue);
      if (kind === "ls") {
        const converted = formatConvertedNb(numValue * 3.6);
        return `${primary} ${title} (${converted} ${LABEL_M3H})`;
      }
      if (kind === "m3h") {
        const converted = formatConvertedNb(numValue / 3.6);
        return `${primary} ${title} (${converted} ${LABEL_LS})`;
      }
      return `${primary} ${title}`;
    }
  }

  if (kind === "ls") {
    return `${title} (${LABEL_M3H})`;
  }
  if (kind === "m3h") {
    return `${title} (${LABEL_LS})`;
  }
  return title;
}

/**
 * Primary + secondary flow pair for UI cards/tables (m³/h ↔ l/s).
 * @returns {{ primary: string|null, secondary: string|null }}
 */
export function formatFlowPairNb(unit, value) {
  if (value === null || value === undefined || value === "") {
    return { primary: null, secondary: null };
  }
  const num = parseFloat(value);
  if (Number.isNaN(num)) return { primary: null, secondary: null };
  if (!unit) {
    return { primary: formatPrimaryNb(num), secondary: null };
  }
  const kind = getFlowRateUnitKind(unit);
  if (kind === "m3h") {
    return {
      primary: `${formatPrimaryNb(num)} ${LABEL_M3H}`,
      secondary: `${formatConvertedNb(num / 3.6)} ${LABEL_LS}`,
    };
  }
  if (kind === "ls") {
    return {
      primary: `${formatPrimaryNb(num)} ${LABEL_LS}`,
      secondary: `${formatConvertedNb(num * 3.6)} ${LABEL_M3H}`,
    };
  }
  const title = unit.title || "";
  return { primary: `${formatPrimaryNb(num)} ${title}`.trim(), secondary: null };
}

/** Display number in m3/h from stored value + its storage flow kind. */
export function flowStorageToM3h(value, storageKind) {
  const n = parseFloat(value);
  if (Number.isNaN(n)) return null;
  if (storageKind === "m3h") return n;
  if (storageKind === "ls") return n * 3.6;
  return n;
}

/** Display number in l/s from stored value + its storage flow kind. */
export function flowStorageToLs(value, storageKind) {
  const n = parseFloat(value);
  if (Number.isNaN(n)) return null;
  if (storageKind === "ls") return n;
  if (storageKind === "m3h") return n / 3.6;
  return n;
}

/** Convert display value (in displayKind) to storage value for targetStorageKind (both m3h|ls). */
export function flowDisplayToStorage(displayVal, displayKind, targetStorageKind) {
  const n = parseFloat(displayVal);
  if (Number.isNaN(n)) return null;
  if (displayKind === targetStorageKind) return n;
  if (displayKind === "m3h" && targetStorageKind === "ls") return n / 3.6;
  if (displayKind === "ls" && targetStorageKind === "m3h") return n * 3.6;
  return n;
}
